import { useNavigate, useSearchParams } from 'react-router-dom'
import { PagePreloader } from '../../../../components/pagePreloader'
import { SmoothTransition } from '../../../../components/pageTransition'
import { ListApiFilterParams } from '../../../../interfaces/api-request'
import {
    getErrorResponse,
    getTimeRelativeTime,
    getUpdatedSearchParams,
    parseSearchParams,
} from '../../../../services/util'
import { ColumnDef, RowSelectionState } from '@tanstack/react-table'
import { useForm } from 'react-hook-form'
import { DefaultListApiResponse } from '../../../../interfaces/api-response'
import { usePopup } from '../../../../context/popupContext'
import { useNotificationBar } from '../../../../context/notificationContext'
import { RenderDefaultActions, RenderStatus } from '../../common'
import { useEffect, useState } from 'react'
import { FilterOptions } from '../../../../components/customTable/custom-table-interfaces'
import { Unauthorized } from '../../../Unauthorized'
import { CustomTable } from '../../../../components/customTable'
import moment from 'moment'
import { GetConfigData } from '../../../../hooks/category-manage'
import { useLanguageData, useUpdateLanguageData, useDeleteLanguageData } from '../../../../hooks/language-master'
import { AddUpdate } from './addLanguage'
import { useDeleteUser } from '../../../../hooks/login-activity'
import { twElementStyles } from '../../../../twElementStyles'

export const LanguageMaster = () => {
    let [searchParams, setSearchParams] = useSearchParams()
    let filterParams: ListApiFilterParams = parseSearchParams(
        searchParams,
        'filter'
    )
    const [addUpdate, setAddUpdate] = useState<any>(null)
    const [addUpdateInput, setaddUpdateInput] = useState<any>(null)
    const [editForm, setEditForm] = useState<any>(false)
    const { data: options } = GetConfigData()
    const status: Object = options?.data?.results?.status ?? {}

    let {
        data,
        isFetching: isLoading,
        isError,
        isSuccess,
        error,
        refetch,
        dataUpdatedAt,
    } = useLanguageData(filterParams)

    let useFormHook = useForm({ defaultValues: filterParams })
    const popup = usePopup()
    const notification = useNotificationBar()

    let { mutate: deleteUser } = useDeleteLanguageData()
    const { mutate: update } = useUpdateLanguageData()

    let responseData: DefaultListApiResponse = data?.data

    const onDeleteAction = (row: any) => {
        const languageName = row?.name || 'this language';

        popup.open({
            title: 'Are you sure ?',
            message: `Do you want to delete ${languageName}? This cannot be undone.`,
            type: 'confirm',
            onAction: (value: boolean) => {
                if (value) {
                    deleteUser(
                        { id: row?.id },
                        {
                            onSuccess: () => {
                                refetch();
                            },

                        }
                    );
                }
            },
            confirmButtonText: 'Delete',
            confirmButtonClass: twElementStyles.twButtonElegantRed,
        });
    };


    const onStatusChange = async (row: any) => {
        let updateValue: any = { status: row.status ? 0 : 1 }
        popup.open({
            title: `${updateValue.status ? 'Activate' : 'Deactivate'} Language ?`,
            message: `Do you want to ${updateValue.status ? 'activate' : 'deactivate'
                } ${row?.name} language?`,
            type: 'confirm',
            onAction: (value: any) => {
                if (value) {
                    update(
                        {
                            id: row.id,
                            data: updateValue,
                        },
                        {
                            onSuccess: () => {
                                notification.push({
                                    title: `Language ${updateValue.status
                                        ? 'Activated !'
                                        : 'Deactivated !'
                                        } `,
                                    message: `Language  ${row.name
                                        } has been ${updateValue.status === 0
                                            ? 'Deactivated'
                                            : 'Actived'
                                        } successfully !`,
                                    type: updateValue.status ? 'success' : 'info',
                                })
                                refetch()
                            },
                            onError: (error) => {
                                notification.push(getErrorResponse(error))
                            },
                        }
                    )
                }
            },
        })
    }

    const openAddUpdateDialog = (input: any, string: any) => {
        setaddUpdateInput(input)
        setAddUpdate(true)
        if (string === 'edit') {
            setEditForm(true)
        } else {
            setEditForm(false)
        }
    }

    const onDataAddUpdateAction = (values: any) => {
        setAddUpdate(false);
        setaddUpdateInput(null);
        refetch();
    }

    const onAction = (actionType: any, row: any) => {
        if (actionType === 'edit') openAddUpdateDialog(row, 'edit')
        if (actionType === 'delete') onDeleteAction(row)
    }

    const columns: ColumnDef<any>[] = [
        {
            accessorKey: 'sr_no',
            header: () => 'ID',
            footer: (info) => info.column.id,
            cell: ({ row, table }) => {
                const pageIndex = table.getState().pagination.pageIndex
                const pageSize = table.getState().pagination.pageSize

                return pageIndex * pageSize + row.index + 1
            },
            enableSorting: false,
        },
        {
            accessorKey: 'name',
            header: () => 'NAME',
            footer: (info) => info.column.id,
        },
        {
            accessorKey: 'code',
            header: () => 'CODE',
            footer: (info) => info.column.id,
        },
        {
            accessorKey: 'createdAt',
            header: () => 'CREATED Date',
            footer: (info) => info.column.id,
            accessorFn: (row) => moment(row.date).format('LL'),
        },
        {
            header: () => 'STATUS',
            id: 'status',
            footer: (info) => info.column.id,
            cell: ({ row }) => RenderStatus(row, 'status', onStatusChange),
        },
        {
            id: 'actions',
            header: 'ACTIONS',
            cell: ({ row }) => RenderDefaultActions(row, ['edit', 'delete'], onAction)
        },
    ]

    const mobileViewOptions = {
        mainTitle: {
            accessKey: 'name',
        },
    }

    const rowSelectionState = useState<RowSelectionState>({})
    useEffect(() => {
        const subscriber = useFormHook.watch((values, { name }) => {
            if (name) {
                let updated = getUpdatedSearchParams(filterParams, values, name)
                if (Object.keys(updated)?.length) {
                    let sp = encodeURIComponent(JSON.stringify(updated))
                    setSearchParams({ filter: sp })
                } else {
                    setSearchParams({})
                }
            }
        })
        return () => subscriber.unsubscribe()
    }, [useFormHook, filterParams, setSearchParams])

    const filterOptions: FilterOptions[] = [
        {
            key: 'status',
            options: [
                {
                    display: `All`,
                    value: '',
                },
                {
                    display: `Active`,
                    value: 1,
                },
                {
                    display: `In Active`,
                    value: 0,
                },
            ],
            label: 'Status',
        },
    ]

    return isSuccess || isError ? (
        <SmoothTransition>
            {(error as any)?.response?.data?.statusCode === 403 ? (
                <Unauthorized />
            ) : (
                <div className="w-full px-6 pt-2 bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-800 shadow-md ">
                    <AddUpdate isEditForm={editForm} onSubmitHandler={onDataAddUpdateAction} input_data={addUpdateInput} isOpen={!!addUpdate} setIsOpen={setAddUpdate} />
                    <aside className="flex flex-auto items-center">
                        <CustomTable
                            title="Language Master"
                            titleLabel={
                                !isError
                                    ? `Last fetched on ${getTimeRelativeTime(
                                        dataUpdatedAt
                                    )}`
                                    : ''
                            }
                            formHook={useFormHook}
                            columns={columns}
                            data={responseData?.results}
                            pages={responseData?.pages}
                            totalRecords={responseData?.total}
                            onRefresh={refetch}
                            isLoading={isLoading}
                            isErrored={isError}
                            rowSelectionState={rowSelectionState}
                            mobileViewOptions={mobileViewOptions}
                            filterOptions={filterOptions}
                            additionalOptions={[]}
                            onAddAction={() => setAddUpdate({})}
                        />
                    </aside>
                </div>
            )}
        </SmoothTransition >
    ) : (
        <PagePreloader />
    )
}
export default LanguageMaster
